import type { BuiltInFormSchema } from '../types/builtInFormSchema';

export const heatingSystemSchema: BuiltInFormSchema = {
  formId: 'heating_system',
  formTitle: 'Heating System & Controls',
  sections: [
    {
      id: 'main_heating',
      title: 'Main Heating Source',
      fields: [
        { 
          id: 'mainHeatingSource', 
          label: 'Main Heating Source', 
          type: 'dropdown', 
          required: true,
          helpText: 'Select the primary heating source for this property',
          options: [
            { value: '', label: 'Select List Value(s)' },
            { value: 'gas_boiler', label: 'Gas Boiler' },
            { value: 'oil_boiler', label: 'Oil Boiler' },
            { value: 'heat_pump', label: 'Heat Pump' }
          ]
        },
      ],
    },
    {
      id: 'boiler_details',
      title: 'Boiler Information',
      fields: [
        { id: 'boilerManufacturer', label: 'Boiler Manufacturer', type: 'text', required: false },
        { id: 'boilerBrand', label: 'Boiler Brand', type: 'text', required: false },
        { id: 'modelQualifier', label: 'Model Qualifier', type: 'text', required: false },
        { id: 'boilerType', label: 'Boiler Type', type: 'dropdown', required: false, options: [
          { value: '', label: 'Select List Value(s)' },
          { value: 'combi', label: 'Combi' },
          { value: 'system', label: 'System' },
          { value: 'regular', label: 'Regular (Heat Only)' }
        ] },
        { id: 'fuelType', label: 'Fuel Type', type: 'dropdown', required: false, options: [
          { value: '', label: 'Select List Value(s)' },
          { value: 'gas', label: 'Gas' },
          { value: 'oil', label: 'Oil' },
          { value: 'lpg', label: 'LPG' },
          { value: 'electric', label: 'Electric' }
        ] },
        { id: 'sapWinterEfficiency', label: 'SAP Winter Efficiency (%)', type: 'text', required: false },
        { id: 'boilerAge', label: 'Boiler Age', type: 'text', required: false },
        { id: 'commissionYear', label: 'Commission Year', type: 'text', required: false },
        { id: 'serialNumber', label: 'Serial Number', type: 'text', required: false },
        { id: 'boilerLocation', label: 'Boiler Location', type: 'dropdown', required: false, options: [
          { value: '', label: 'Select List Value(s)' },
          { value: 'kitchen', label: 'Kitchen' },
          { value: 'utility_room', label: 'Utility Room' },
          { value: 'airing_cupboard', label: 'Airing Cupboard' },
          { value: 'garage', label: 'Garage' },
          { value: 'loft', label: 'Loft' }
        ] },
        { id: 'boilerPosition', label: 'Boiler Position', type: 'dropdown', required: false, options: [
          { value: '', label: 'Select List Value(s)' },
          { value: 'wall_mounted', label: 'Wall Mounted' },
          { value: 'floor_mounted', label: 'Floor Mounted' },
          { value: 'other', label: 'Other' }
        ] },
        { 
          id: 'boilerPositionOther', 
          label: 'Specify Other Position', 
          type: 'text', 
          required: false,
          conditionalOn: {
            fieldId: 'boilerPosition',
            value: 'other'
          }
        },
        { id: 'heatingSystemCondition', label: 'Heating System Condition', type: 'dropdown', required: false, options: [
          { value: '', label: 'Select List Value(s)' },
          { value: 'good', label: 'Good' },
          { value: 'fair', label: 'Fair' },
          { value: 'poor', label: 'Poor' }
        ] },
      ],
    },
    {
      id: 'boiler_photos',
      title: 'Boiler Photos',
      fields: [
        { id: 'currentBoilerImage', label: 'Current Boiler', type: 'photo', required: false },
        { id: 'boilerDataBadgeImage', label: 'Boiler Data Badge', type: 'photo', required: false },
        { id: 'fluePipeImage', label: 'Flue Pipe', type: 'photo', required: false },
      ],
    },
    {
      id: 'additional_equipment',
      title: 'Additional Equipment',
      fields: [
        { id: 'hasCondensatePipe', label: 'Does the property have a condensate pipe?', type: 'boolean', required: false },
        { 
          id: 'condensatePipeImage', 
          label: 'Condensate Pipe Photos', 
          type: 'photo', 
          required: false,
          conditionalOn: {
            fieldId: 'hasCondensatePipe',
            value: true
          }
        },
        { id: 'hasOilLpgTank', label: 'Does the property have an oil/LPG tank?', type: 'boolean', required: false },
        { 
          id: 'oilLpgTankImage', 
          label: 'Oil/LPG Tank Photos', 
          type: 'photo', 
          required: false,
          conditionalOn: {
            fieldId: 'hasOilLpgTank',
            value: true
          }
        },
        { id: 'hasRenewables', label: 'Does the property have renewable energy systems?', type: 'boolean', required: false },
        { 
          id: 'renewablesImage', 
          label: 'Renewables Photos', 
          type: 'photo', 
          required: false,
          conditionalOn: {
            fieldId: 'hasRenewables',
            value: true
          }
        },
        { id: 'hasCylinderWaterHeating', label: 'Does the property have a cylinder/water heating tank?', type: 'boolean', required: false },
        { 
          id: 'cylinderWaterHeatingImage', 
          label: 'Cylinder/Water Heating Photos', 
          type: 'photo', 
          required: false,
          conditionalOn: {
            fieldId: 'hasCylinderWaterHeating',
            value: true
          }
        },
        { id: 'hasOtherBoilers', label: 'Are there other boilers in the property?', type: 'boolean', required: false },
        { 
          id: 'otherBoilersDetails', 
          label: 'Other Boilers Details', 
          type: 'textarea', 
          required: false,
          conditionalOn: {
            fieldId: 'hasOtherBoilers',
            value: true
          }
        },
      ],
    },
    {
      id: 'heating_controls',
      title: 'Heating Controls',
      fields: [
        { id: 'fullSetFunctioningControls', label: 'Does the system have a full set of functioning controls?', type: 'boolean', required: false },
        { id: 'preExistingHeatingControlsImages', label: 'Pre-existing Heating Controls Photos', type: 'photo', required: false },
        { id: 'systemHasBypassRadiator', label: 'Does the system have a bypass radiator?', type: 'boolean', required: false },
        { 
          id: 'bypassRadiatorImage', 
          label: 'Bypass Radiator Photos', 
          type: 'photo', 
          required: false,
          conditionalOn: {
            fieldId: 'systemHasBypassRadiator',
            value: true
          }
        },
      ],
    },
  ],
};
